import isEqual from "lodash.isequal";
import { URLSearchParams } from "../internal/url-search-params";
import { createParamsFromConfig, createShowroomPath, createSubpaths, equalString, getAbsoluteNodeId, getNodeId, getPrimaryPath, getRelativeNodeId, readParamsToConfig } from "./url-utils";
const ANCHOR_CONFIGURE = "CONFIGURE";
const ANCHOR_MOFA = "MOFA";
export const normalizeConfig = (config) => {
    if (config) {
        const untypedConfig = config;
        /*tslint:disable-next-line*/
        const newConfig = {};
        const configKeys = Object.keys(config); //
        configKeys
            .filter(key => typeof untypedConfig[key] !== "undefined")
            .filter(key => untypedConfig[key] !== null)
            .forEach(key => {
            if (key !== "buildabilityStatus") {
                const newValue = untypedConfig[key];
                if (Array.isArray(newValue) && newValue.length === 0) {
                    return;
                }
                newConfig[key] = newValue;
            }
        });
        return newConfig;
    }
    return config;
};
export function isConfigurationEqual(config1, config2) {
    const equal = isEqual(normalizeConfig(config1), normalizeConfig(config2));
    return equal;
}
class NavigationTargetV1Impl {
    constructor(path, search, hash, history) {
        this.path = path;
        this.search = search;
        this.hash = hash;
        this.history = history;
    }
    createHref() {
        return `${this.path}${this.search}${this.hash}`;
    }
    push() {
        if (this.history) {
            const pattern = /^((http|https):\/\/)/;
            if (pattern.test(this.path)) {
                window.location.href = this.path;
            }
            else {
                this.history.push({
                    pathname: this.path,
                    hash: this.hash,
                    search: this.search
                });
            }
        }
    }
    replace() {
        if (this.history) {
            if (this.path.startsWith("http")) {
                window.location.href = this.path;
            }
            else {
                this.history.replace({
                    pathname: this.path,
                    hash: this.hash,
                    search: this.search
                });
            }
        }
    }
    matchesLocation(location) {
        return (equalString(this.search || "", location.search) &&
            equalString(this.path, location.pathname));
    }
}
const getSuffixNodeId = (nodeId, showroomConfig) => {
    const suffix = nodeId !== showroomConfig.nodeId && nodeId !== showroomConfig.defaultNodeId
        ? getRelativeNodeId(nodeId, showroomConfig.nodeId)
        : undefined;
    return suffix;
};
/**
 * only compares trim values expecting them to be defined
 */
const equalTrim = (trim1, trim2) => {
    const equal = trim1.tenant === trim2.tenant &&
        trim1.carlineId === trim2.carlineId &&
        trim1.salesGroupId === trim2.salesGroupId &&
        trim1.trimName === trim2.trimName;
    return equal;
};
export class ShowroomNavigationManagerV1Impl {
    constructor(config, carConfigurationService, historyService, extension = "app", replaceOnSync = false, noSuffix = false) {
        this.config = config;
        this.carConfigurationService = carConfigurationService;
        this.extension = extension;
        this.replaceOnSync = replaceOnSync;
        this.noSuffix = noSuffix;
        this.history = historyService.history;
        this.configureAnchor = config.configureAsAnchor
            ? ANCHOR_CONFIGURE
            : ANCHOR_MOFA;
        this.initCarConfigurationService(this.history.location);
        if (!this.noSuffix) {
            this.carConfigurationService.subscribe((event) => {
                switch (event) {
                    case "createBuildable":
                        // do not sync createBuildable into url
                        // 1. happens really quickly after load and influence page load event
                        // 2. default buildable doesn't need to be bookmarkable.
                        // this.syncConfigurationToUrl(true);
                        break;
                    case "set":
                    case "setDefaultContext":
                    default:
                        this.syncConfigurationToUrl(this.replaceOnSync);
                }
            });
        }
        this.history.listen(update => {
            this.onEvent(update.location);
        });
    }
    createShowroomNavigation(nodeId) {
        if (typeof nodeId === "string") {
            return this.createShowroomNavigationInternally(nodeId);
        }
        return this.createConfigurationNavigationInternally(nodeId);
    }
    createConfigureShowroomNavigation(nodeId, anchor) {
        if (typeof nodeId === "string") {
            return this.createShowroomNavigationInternally(nodeId, anchor || ANCHOR_MOFA);
        }
        return this.createConfigurationNavigationInternally(nodeId, anchor || this.configureAnchor);
    }
    createShowroomNavigationInternally(nodeId, anchor) {
        // configure links are only available for non-virtual trims
        if (anchor) {
            const trimConfig = this.config.trimConfigs[nodeId];
            if (trimConfig && trimConfig.tenant === "virtual") {
                return undefined;
            }
        }
        // find a showroom either in the hierachy of given nodeId or in identical trim's hierachies.
        const result = this.resolveShowroomConfig(nodeId);
        if (result) {
            return this.createNavigationTargetForShowroom(result.nodeId, anchor, result.showroomConfig);
        }
        // check if a simple link is configured
        const link = this.config.nodeLinks[nodeId];
        if (link && !anchor) {
            return new NavigationTargetV1Impl(link, "", "", this.history);
        }
        return undefined;
    }
    get showroomNodeId() {
        if (!this.history) {
            return undefined;
        }
        const primaryPath = getPrimaryPath(this.history.location.pathname);
        const nodeId = this.config.showroomMap[primaryPath];
        return nodeId;
    }
    // just link to the showroom
    createSelectModelInShowroomNavigation(currentShowroomNodeId, nodeId, navigateToConfigureApp = true) {
        if (!currentShowroomNodeId) {
            console.warn(`cannot switch to model as this is not a showroom`);
            return undefined;
        }
        // relative node id will be 'undefined' if nodeId is not beyond the showroomNodeId
        // if relative node id is empty, it means that nodeId is equal to the showroomNodeId
        const relativeNodeId = getRelativeNodeId(nodeId, currentShowroomNodeId);
        if (typeof relativeNodeId === "undefined") {
            console.warn(`cannot switch to ${nodeId} as it is not child of current showroom ${currentShowroomNodeId}.`);
            return undefined;
        }
        const showroomConfig = this.findMappedShowroomInAncestors(currentShowroomNodeId);
        if (!showroomConfig) {
            console.warn(`no showroom exists for ${currentShowroomNodeId}`);
            return undefined;
        }
        const trimConfig = this.config.trimConfigs[nodeId];
        if (!trimConfig || trimConfig.tenant === "virtual") {
            return undefined;
        }
        const suffixNodeId = getSuffixNodeId(nodeId, showroomConfig);
        const fullPath = createShowroomPath({
            pathname: showroomConfig.path,
            nodeId: suffixNodeId
        }, this.extension, this.noSuffix);
        return new NavigationTargetV1Impl(fullPath, "", navigateToConfigureApp ? `#${ANCHOR_CONFIGURE}` : "", this.history);
    }
    resolveShowroomConfig(nodeId) {
        // make sure that showroomConfig exists for nodeId
        const showroomConfig = this.findMappedShowroomInAncestors(nodeId);
        if (showroomConfig) {
            // check if the nodeId is the showroom
            if (nodeId === showroomConfig.nodeId) {
                return {
                    nodeId,
                    showroomConfig
                };
            }
            // check if the nodeId is a child of the showroom
            const selectedNodeId = getRelativeNodeId(nodeId, showroomConfig.nodeId);
            if (selectedNodeId) {
                return {
                    nodeId,
                    showroomConfig
                };
            }
        }
        else {
            // the showroom is not connected to the nodeId or its ancestor. check via the trimconfigs if the same trim exists in a showroom
            if (this.isTrim(nodeId)) {
                const identicalTrimNodeIds = this.findIdenticalTrimNodes(nodeId);
                const otherShowromConfig = this.findFirstShowroomConfig(identicalTrimNodeIds);
                if (otherShowromConfig) {
                    return {
                        nodeId: otherShowromConfig.nodeId,
                        showroomConfig: otherShowromConfig.showroomConfig
                    };
                }
            }
        }
        return undefined;
    }
    isTrim(nodeId) {
        return !!this.config.trimConfigs[nodeId];
    }
    createNavigationTargetForShowroom(nodeId, anchor, showroomConfig) {
        const selectedNodeId = getSuffixNodeId(nodeId, showroomConfig);
        const fullPath = createShowroomPath({
            pathname: showroomConfig.path,
            nodeId: selectedNodeId
        }, this.extension, this.noSuffix);
        return new NavigationTargetV1Impl(fullPath, "", anchor ? "#" + anchor : "", this.history);
    }
    findFirstShowroomConfig(searchNodes) {
        for (const nodeId of searchNodes) {
            const showroomConfig = this.findMappedShowroomInAncestors(nodeId);
            if (showroomConfig) {
                return { showroomConfig, nodeId };
            }
        }
        return undefined;
    }
    findIdenticalTrimNodes(nodeId) {
        const trim = this.config.trimConfigs[nodeId];
        if (!trim) {
            return [];
        }
        const identialTrimNodes = [];
        Object.keys(this.config.trimConfigs).forEach((possibleIdenticalNodeId) => {
            if (possibleIdenticalNodeId !== nodeId &&
                equalTrim(trim, this.config.trimConfigs[possibleIdenticalNodeId])) {
                identialTrimNodes.push(possibleIdenticalNodeId);
            }
        });
        return identialTrimNodes;
    }
    getConfig(location) {
        const primaryPath = getPrimaryPath(location.pathname);
        const showroomNodeId = this.config.showroomMap[primaryPath];
        if (!showroomNodeId) {
            return undefined;
        }
        // get config for showroom
        const showroomConfig = this.config.showroomConfigs[showroomNodeId];
        if (!showroomConfig) {
            return undefined;
        }
        // get nodeId of selected model
        const currentNodeId = getNodeId(location.pathname);
        const nodeId = currentNodeId
            ? getAbsoluteNodeId(currentNodeId, showroomNodeId)
            : showroomConfig.defaultNodeId;
        // get trim config of selected model
        const trimConfig = this.config.trimConfigs[nodeId];
        if (trimConfig) {
            const fullConfig = readParamsToConfig(new URLSearchParams(location.search), trimConfig);
            return fullConfig;
        }
        return undefined;
    }
    createConfigurationNavigationInternally(config, anchor) {
        const trimNodeIds = this.getTrimNodeIds(config);
        if (trimNodeIds.length < 1) {
            return undefined;
        }
        // find a showroom that is mapped to the trim or its ancestors
        let showroomConfig;
        let selectTrimNodeId;
        for (const trimNodeId of trimNodeIds) {
            showroomConfig = this.findMappedShowroomInAncestors(trimNodeId);
            selectTrimNodeId = trimNodeId;
            if (showroomConfig) {
                break;
            }
        }
        if (!showroomConfig || !selectTrimNodeId) {
            return undefined;
        }
        // we found a showroom
        const suffixNodeId = getSuffixNodeId(selectTrimNodeId, showroomConfig);
        const showroomPath = {
            pathname: showroomConfig.path,
            nodeId: suffixNodeId
        };
        return this.navigate(showroomPath, config, anchor);
    }
    /**
     * finds the showroom that maps to this node or its ancestors
     */
    findMappedShowroomInAncestors(trimNodeId) {
        // create nodeIds for all ancestors
        const candidatePaths = createSubpaths(trimNodeId);
        let showroomConfig;
        let showroomNodeId;
        for (let i = 0; i < candidatePaths.length && !showroomConfig; i++) {
            showroomNodeId = candidatePaths[i];
            showroomConfig = this.config.showroomConfigs[showroomNodeId];
            // check that showroom exists and is mapped that node.
            if (showroomConfig &&
                this.config.showroomMap[showroomConfig.path] === showroomNodeId) {
                return showroomConfig;
            }
        }
        return undefined;
    }
    onEvent(location) {
        const currentCtx = this.carConfigurationService.getContext();
        if (currentCtx &&
            Boolean(currentCtx.context) &&
            getPrimaryPath(location.pathname) === currentCtx.context) {
            // still the same path
            return;
        }
        if (currentCtx &&
            Boolean(currentCtx.context) &&
            getPrimaryPath(location.pathname) === this.lastContext) {
            // if the context was changed by configure on the same page, we shouldn't initialize the ccs
            return;
        }
        this.initCarConfigurationService(location);
    }
    initCarConfigurationService(location) {
        const config = this.getConfig(location);
        this.lastContext = getPrimaryPath(location.pathname);
        this.carConfigurationService.setDefaultContext(this.lastContext, config && config.tenant !== "virtual" ? config : undefined);
    }
    getTrimNodeIds(config) {
        const navigableNodeIds = [];
        Object.keys(this.config.trimConfigs).forEach(nodeId => {
            const navigableTrim = this.config.trimConfigs[nodeId];
            if (equalTrim(navigableTrim, config)) {
                navigableNodeIds.push(nodeId);
            }
        });
        return navigableNodeIds;
    }
    syncConfigurationToUrl(replace) {
        const newConfig = this.carConfigurationService.get();
        if (newConfig == null) {
            return;
        }
        const showroomPath = getPrimaryPath(this.history.location.pathname);
        const parentNodeId = this.config.showroomMap[showroomPath];
        if (!parentNodeId) {
            // not in a showroom
            return;
        }
        const trimNodeIds = this.getTrimNodeIds(newConfig);
        if (trimNodeIds.length === 0) {
            return;
        }
        // new nodeId must be child of old one
        const navigableNodeIds = trimNodeIds.filter(nodeId => nodeId.startsWith(parentNodeId));
        if (navigableNodeIds.length === 1) {
            const showroomConfig = Object.assign(Object.assign({}, this.config.showroomConfigs[parentNodeId]), { nodeId: parentNodeId });
            const suffixNodeId = getSuffixNodeId(navigableNodeIds[0], showroomConfig);
            const navigationTarget = this.navigate({ pathname: showroomPath, nodeId: suffixNodeId }, newConfig);
            const currentLocation = this.history.location;
            if (navigationTarget &&
                !navigationTarget.matchesLocation(currentLocation)) {
                if (replace) {
                    navigationTarget.replace();
                }
                else {
                    navigationTarget.push();
                }
            }
        }
    }
    navigate(showroomPath, config, anchor) {
        const params = createParamsFromConfig(config);
        const pathname = createShowroomPath(showroomPath, this.extension, this.noSuffix);
        const hash = anchor ? `#${anchor}` : "";
        const search = params.toString().length > 0 ? `?${params.toString()}` : "";
        return new NavigationTargetV1Impl(pathname, search, hash, this.history);
    }
}
